// File: frontend/src/utils/Pagination.tsx

import "./Pagination.css";
import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

export const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  totalItems: number;
  paginate: (pageNumber: number) => void;
}> = (props) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState<"down" | "up">("down");
  const dropdownRef = useRef<HTMLLIElement>(null);
  // Manual "go to page" input — lets the user jump straight to e.g. page 700
  // instead of scrolling the whole number list
  const [pageInput, setPageInput] = useState("");
  const [inputInvalid, setInputInvalid] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const pageNumbers = props.totalPages > 0 
    ? Array.from({ length: props.totalPages }, (_, i) => i + 1)
    : [];

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const dropdownElement = dropdownRef.current;
      const rect = dropdownElement.getBoundingClientRect();
      
      // Check if there's enough space below (with some buffer)
      const spaceBelow = window.innerHeight - rect.bottom - 200;
      const spaceAbove = rect.top - 200;
      
      // Open upwards if there's more space above or not enough below
      setDropdownDirection(spaceBelow < 100 && spaceAbove > spaceBelow ? "up" : "down");
    }
  }, [isOpen]);

  // When the menu opens: focus the jump input and scroll the current page into
  // view, so long lists don't always start at page 1
  useEffect(() => {
    if (!isOpen) {
      setPageInput("");
      setInputInvalid(false);
      return;
    }
    inputRef.current?.focus();
    const active = menuRef.current?.querySelector(".dropdown-item.active");
    active?.scrollIntoView({ block: "center" });
  }, [isOpen]);

  // The menu now holds a text field, so a click anywhere outside must close it
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Applies the manually typed page number when it is a valid page
  const submitPageInput = useCallback(() => {
    const parsed = parseInt(pageInput.trim(), 10);
    if (isNaN(parsed) || parsed < 1 || parsed > props.totalPages) {
      setInputInvalid(true);
      inputRef.current?.select();
      return;
    }
    setInputInvalid(false);
    setIsOpen(false);
    if (parsed !== props.currentPage) {
      props.paginate(parsed);
    }
  }, [pageInput, props]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitPageInput();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  // Don't render pagination if there are no items or only one page
  if (props.totalItems <= 0 || props.totalPages <= 1) {
    return null;
  }

  const isFirst = props.currentPage <= 1;
  const isLast = props.currentPage >= props.totalPages;

  return (
    <nav aria-label="Page navigation">
      <ul className="pagination">
        {/* First page */}
        <li
          className={`page-item ${isFirst ? "disabled" : ""}`}
          onClick={() => !isFirst && props.paginate(1)}
        >
          <button className="page-link" disabled={isFirst}>«</button>
        </li>

        {/* Previous page */}
        <li
          className={`page-item ${isFirst ? "disabled" : ""}`}
          onClick={() => !isFirst && props.paginate(props.currentPage - 1)}
        >
          <button className="page-link" disabled={isFirst}>‹</button>
        </li>
        
        {/* Page selector dropdown with ref */}
        <li className="page-item dropdown" ref={dropdownRef}>
          <button 
            className="page-link dropdown-toggle" 
            onClick={() => setIsOpen(!isOpen)}
          >
            {props.currentPage} / {props.totalPages}
          </button>
          
          {/* Dropdown menu with dynamic direction */}
          {isOpen && (
            <div className={`dropdown-menu ${dropdownDirection}`} ref={menuRef}>
              {/* Manual page jump — stays pinned at the top of the scrolling list */}
              <div
                className="pagination-jump"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  ref={inputRef}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={props.totalPages}
                  className={`pagination-jump__input ${inputInvalid ? "pagination-jump__input--invalid" : ""}`}
                  placeholder={t("pagination_jump_placeholder", "sayfa")}
                  aria-label={t("pagination_jump_label", "Sayfa numarası gir")}
                  value={pageInput}
                  onChange={(e) => { setPageInput(e.target.value); setInputInvalid(false); }}
                  onKeyDown={handleInputKeyDown}
                />
                <button
                  type="button"
                  className="pagination-jump__btn"
                  onClick={submitPageInput}
                >
                  {t("pagination_jump_go", "git")}
                </button>
              </div>

              {pageNumbers.map((number) => (
                <div
                  key={number}
                  onClick={(e) => {
                    e.stopPropagation();
                    props.paginate(number);
                    setIsOpen(false);
                  }}
                  className={
                    "dropdown-item " + (props.currentPage === number ? "active" : "")
                  }
                >
                  {number}
                </div>
              ))}
            </div>
          )}
        </li>

        {/* Next page */}
        <li
          className={`page-item ${isLast ? "disabled" : ""}`}
          onClick={() => !isLast && props.paginate(props.currentPage + 1)}
        >
          <button className="page-link" disabled={isLast}>›</button>
        </li>
        
        {/* Last page */}
        <li
          className={`page-item ${isLast ? "disabled" : ""}`}
          onClick={() => !isLast && props.paginate(props.totalPages)}
        >
          <button className="page-link" disabled={isLast}>»</button>
        </li>
      </ul>
    </nav>
  );
};